// Subsea BOP Test Report — record-support only.
// Never certifies pass/fail, BOP condition, fitness-for-service, well safety,
// or shear capability. User-entered values only. Missing fields → "Not entered."
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v != null && String(v).trim() ? String(v).trim() : MISSING);
const polishedOrMissing = (v) => polishProse(v) || MISSING;
const yn = (v) => (v === true || v === "yes" || v === "Yes" ? "Yes" : v === false || v === "no" || v === "No" ? "No" : v ? String(v) : MISSING);

const sec = (lines, label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

export function buildSubseaBopTestReport(state) {
  const h = state.header || {};
  const stack = state.stack || {};
  const scope = state.scope || {};
  const components = Array.isArray(state.components) ? state.components : [];
  const annular = state.annular || {};
  const ram = state.ram || {};
  const shear = state.shear || {};
  const choke = state.choke || {};
  const manifold = state.manifold || {};
  const pods = state.pods || {};
  const accum = state.accum || {};
  const rov = state.rov || {};
  const summary = state.summary || {};
  const obs = state.obs || {};
  const witness = state.witness || {};
  const notes = state.notes || {};
  const lines = [];
  const push = (s) => lines.push(s);

  // 1. Report Header
  sec(lines, "Report Header");
  push(`Report Date: ${orMissing(h.reportDate)}  ·  Report #: ${orMissing(h.reportNumber)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Contractor: ${orMissing(h.contractor)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Well: ${orMissing(h.wellName)}  ·  Field / Block: ${orMissing(h.fieldBlock)}`);
  push(`Location: ${orMissing(h.location)}  ·  Country / Region: ${orMissing(h.country)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Position: ${orMissing(h.position)}`);
  push(`Tour / Shift: ${orMissing(h.tour)}`);
  push(`Period Start: ${orMissing(h.periodStart)}  ·  Period End: ${orMissing(h.periodEnd)}`);
  push(`Unit System (user-entered): ${orMissing(h.unitSystem)}`);
  push(`Test Procedure Reference: ${orMissing(h.procedureRef)}`);
  push(`Client / Operator Representative: ${orMissing(h.clientRep)}`);
  push(`Drilling Supervisor / Company Rep: ${orMissing(h.companyRep)}`);
  push(`Toolpusher / OIM: ${orMissing(h.toolpusher)}`);
  push(`Subsea Engineer: ${orMissing(h.subseaEng)}`);
  push(`BOP Technician: ${orMissing(h.bopTech)}`);

  // 2. Subsea Stack Information
  sec(lines, "Subsea Stack Information");
  push(`BOP Stack Type: ${orMissing(stack.stackType)}  ·  Stack S/N or ID: ${orMissing(stack.stackId)}`);
  push(`Stack Configuration: ${polishedOrMissing(stack.configuration)}`);
  push(`Water Depth: ${orMissing(stack.waterDepth)}  ·  RKB → Sea Level: ${orMissing(stack.rkbToSea)}`);
  push(`Mudline Depth: ${orMissing(stack.mudlineDepth)}  ·  Stack Depth Reference: ${orMissing(stack.stackDepthRef)}`);
  push(`LMRP Installed: ${yn(stack.lmrpInstalled)}  ·  LMRP ID: ${orMissing(stack.lmrpId)}`);
  push(`Wellhead Connector Type: ${orMissing(stack.whConnectorType)}`);
  push(`Wellhead Connector Status (user-entered): ${orMissing(stack.whConnectorStatus)}`);
  push(`Stack Connector Status (user-entered): ${orMissing(stack.stackConnectorStatus)}`);
  push(`Stack Run Date: ${orMissing(stack.runDate)}  ·  Latched: ${orMissing(stack.latchedAt)}`);
  push(`Last Full BOP Test Date: ${orMissing(stack.lastFullTest)}  ·  Last Function Test: ${orMissing(stack.lastFunctionTest)}`);
  push(`Stack Notes: ${polishedOrMissing(stack.notes)}`);

  // 3. Test Scope
  sec(lines, "Test Scope");
  push(`Test Type: ${orMissing(scope.testType)}`);
  push(`Test Reason: ${polishedOrMissing(scope.testReason)}`);
  push(`Test Medium: ${orMissing(scope.testMedium)}  ·  Test Pump: ${orMissing(scope.testPump)}`);
  push(`Test Line Used: ${orMissing(scope.testLine)}`);
  push(`Chart / Recorder Reference: ${orMissing(scope.chartRef)}`);
  push(`Digital Recording Reference: ${orMissing(scope.digitalRef)}`);
  push(`Test Started: ${orMissing(scope.startTime)}  ·  Completed: ${orMissing(scope.endTime)}`);
  push(`Test Status (user-entered only): ${orMissing(scope.userStatus)}`);
  push(`Rough Notes: ${polishedOrMissing(scope.roughNotes)}`);

  // 4. BOP Stack Components Tested
  sec(lines, "BOP Stack Components Tested");
  if (!components.length) {
    push("No component rows entered. Add a row to record each BOP component tested.");
  } else {
    components.forEach((c, i) => {
      const idx = i + 1;
      push(`-- Component ${idx} --`);
      push(`Name: ${orMissing(c.name)}  ·  Type: ${orMissing(c.componentType)}`);
      push(`Open / Close Function Recorded: ${yn(c.fnRecorded)}`);
      push(`Low-Pressure Test (user-entered): ${orMissing(c.lowP)}  ·  High-Pressure Test: ${orMissing(c.highP)}`);
      push(`Hold Period: ${orMissing(c.hold)}  ·  Pressure Drop Observed: ${orMissing(c.pressureDrop)}`);
      push(`Bleed-Off Observed: ${orMissing(c.bleedOff)}`);
      push(`Leak Observed (user-entered): ${yn(c.leak)}`);
      push(`Status (user-entered, not classified by tool): ${orMissing(c.status)}`);
      push(`Remarks: ${polishedOrMissing(c.remarks)}`);
    });
  }
  push("");
  push("Component test records are recorded as user-entered observations only. This tool does not determine pass / fail and does not classify any component as serviceable.");

  // 5. Annular Preventer Test Record
  sec(lines, "Annular Preventer Test Record");
  push(`Annular Tested: ${yn(annular.tested)}`);
  push(`Low Test Pressure: ${orMissing(annular.lowP)}  ·  High Test Pressure: ${orMissing(annular.highP)}`);
  push(`Hold Duration: ${orMissing(annular.hold)}`);
  push(`Closing Pressure: ${orMissing(annular.closingP)}  ·  Opening Pressure: ${orMissing(annular.openingP)}`);
  push(`Regulator Setting: ${orMissing(annular.regulator)}`);
  push(`Pressure Drop Observed: ${orMissing(annular.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(annular.leak)}`);
  push(`Status (user-entered): ${orMissing(annular.status)}`);
  push(`Remarks: ${polishedOrMissing(annular.remarks)}`);

  // 6. Ram Preventer Test Record
  sec(lines, "Ram Preventer Test Record");
  push(`Ram Type Tested: ${orMissing(ram.ramType)}`);
  push(`Pipe Size / Test Pipe: ${orMissing(ram.pipeSize)}`);
  push(`Ram Cavity / Position: ${orMissing(ram.cavity)}`);
  push(`Low-Pressure Test: ${orMissing(ram.lowP)}  ·  High-Pressure Test: ${orMissing(ram.highP)}`);
  push(`Hold Duration: ${orMissing(ram.hold)}`);
  push(`Closing Pressure: ${orMissing(ram.closingP)}  ·  Opening Pressure: ${orMissing(ram.openingP)}`);
  push(`Pressure Drop Observed: ${orMissing(ram.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(ram.leak)}`);
  push(`Status (user-entered): ${orMissing(ram.status)}`);
  push(`Remarks: ${polishedOrMissing(ram.remarks)}`);

  // 7. Blind Shear / Shear Ram Record
  sec(lines, "Blind Shear / Shear Ram Record");
  push(`Shear Ram Tested: ${yn(shear.tested)}`);
  push(`Function Test Only: ${yn(shear.functionOnly)}`);
  push(`Pressure Test Performed: ${yn(shear.pressureTested)}`);
  push(`Test Pressure: ${orMissing(shear.testP)}  ·  Hold Duration: ${orMissing(shear.hold)}`);
  push(`Closing Pressure: ${orMissing(shear.closingP)}  ·  Opening Pressure: ${orMissing(shear.openingP)}`);
  push(`Shear Test Performed (user-entered): ${yn(shear.shearPerformed)}`);
  push(`Test Object / Pipe Details: ${polishedOrMissing(shear.testObject)}`);
  push(`Status (user-entered): ${orMissing(shear.status)}`);
  push(`Remarks: ${polishedOrMissing(shear.remarks)}`);
  push("");
  push("This tool does not certify shear capability. Only user-entered test record information is shown.");

  // 8. Choke / Kill Line Test Record
  sec(lines, "Choke / Kill Line Test Record");
  push(`Choke Line Tested: ${yn(choke.chokeTested)}  ·  Kill Line Tested: ${yn(choke.killTested)}`);
  push(`Choke Line Test Pressure: ${orMissing(choke.chokeP)}  ·  Kill Line Test Pressure: ${orMissing(choke.killP)}`);
  push(`Hold Duration: ${orMissing(choke.hold)}`);
  push(`Pressure Drop Observed: ${orMissing(choke.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(choke.leak)}`);
  push(`Choke Line Volume: ${orMissing(choke.chokeVol)}  ·  Kill Line Volume: ${orMissing(choke.killVol)}`);
  push(`Test Direction / Flow Path: ${orMissing(choke.flowPath)}`);
  push(`Status (user-entered): ${orMissing(choke.status)}`);
  push(`Remarks: ${polishedOrMissing(choke.remarks)}`);

  // 9. Choke Manifold / Valves
  sec(lines, "Choke Manifold / Valve Record");
  push(`Choke Manifold Tested: ${yn(manifold.manifoldTested)}`);
  push(`HCR Valves Tested: ${yn(manifold.hcrTested)}  ·  Manual Valves Tested: ${yn(manifold.manualTested)}  ·  Remote Valves Tested: ${yn(manifold.remoteTested)}`);
  push(`Test Pressure: ${orMissing(manifold.testP)}  ·  Hold Duration: ${orMissing(manifold.hold)}`);
  push(`Pressure Drop Observed: ${orMissing(manifold.pressureDrop)}`);
  push(`Leak Observed (user-entered): ${yn(manifold.leak)}`);
  push(`Status (user-entered): ${orMissing(manifold.status)}`);
  push(`Remarks: ${polishedOrMissing(manifold.remarks)}`);

  // 10. Control Pods / Control System
  sec(lines, "Control Pod / Control System Record");
  push(`Blue Pod Status (user-entered): ${orMissing(pods.blueStatus)}`);
  push(`Yellow Pod Status (user-entered): ${orMissing(pods.yellowStatus)}`);
  push(`Active Pod: ${orMissing(pods.activePod)}`);
  push(`Pod Function Test Performed: ${yn(pods.fnTested)}`);
  push(`Pod Response Time Recorded: ${yn(pods.responseRecorded)}`);
  push(`Function Response Notes: ${polishedOrMissing(pods.responseNotes)}`);
  push(`Control Fluid Type: ${orMissing(pods.fluidType)}`);
  push(`Control Fluid Level: ${orMissing(pods.fluidLevel)}  ·  Pressure: ${orMissing(pods.fluidPressure)}`);
  push(`Surface Control Panel Status (user-entered): ${orMissing(pods.surfacePanel)}`);
  push(`MUX / Control System Status (user-entered): ${orMissing(pods.muxStatus)}`);
  push(`Acoustic Backup Status (if applicable): ${orMissing(pods.acoustic)}`);
  push(`Deadman / Autoshear Status (user-entered): ${orMissing(pods.deadmanStatus)}`);
  push(`EDS Status (user-entered): ${orMissing(pods.edsStatus)}`);
  push(`Status (user-entered): ${orMissing(pods.status)}`);
  push(`Remarks: ${polishedOrMissing(pods.remarks)}`);
  push("");
  push("This tool does not certify pod readiness or control-system reliability.");

  // 11. Accumulator / Hydraulic System
  sec(lines, "Accumulator / Hydraulic System Record");
  push(`Accumulator Pressure: ${orMissing(accum.pressure)}  ·  Pre-Charge Pressure: ${orMissing(accum.preCharge)}`);
  push(`Bottle Bank Status: ${orMissing(accum.bottleStatus)}`);
  push(`Usable Fluid Volume (user-entered): ${orMissing(accum.usableVol)}`);
  push(`Closing Unit Status (user-entered): ${orMissing(accum.closingUnit)}`);
  push(`Charge Pumps Status (user-entered): ${orMissing(accum.chargePumps)}`);
  push(`Regulator Settings: ${orMissing(accum.regulators)}`);
  push(`Hydraulic Leaks Observed (user-entered): ${yn(accum.leaks)}`);
  push(`Function Test Notes: ${polishedOrMissing(accum.fnNotes)}`);
  push(`Status (user-entered): ${orMissing(accum.status)}`);
  push(`Remarks: ${polishedOrMissing(accum.remarks)}`);
  push("");
  push("This tool does not certify accumulator capacity or regulatory compliance.");

  // 12. ROV / Emergency Intervention
  sec(lines, "ROV / Emergency Intervention Record");
  push(`ROV Panel Available: ${yn(rov.panelAvailable)}`);
  push(`ROV Hot Stab Status (user-entered): ${orMissing(rov.hotStabStatus)}`);
  push(`ROV Intervention Function Test Performed: ${yn(rov.fnTested)}`);
  push(`ROV Crew Informed: ${yn(rov.crewInformed)}`);
  push(`ROV Access Notes: ${polishedOrMissing(rov.accessNotes)}`);
  push(`Emergency Disconnect Notes: ${polishedOrMissing(rov.edsNotes)}`);
  push(`Intervention Notes: ${polishedOrMissing(rov.interventionNotes)}`);
  push(`Status (user-entered): ${orMissing(rov.status)}`);
  push(`Remarks: ${polishedOrMissing(rov.remarks)}`);
  push("");
  push("This tool does not certify emergency intervention readiness.");

  // 13. Pressure Test Summary
  sec(lines, "Pressure Test Summary");
  push(`Test Pressure Units: ${orMissing(summary.units)}`);
  push(`Lowest Recorded Pressure: ${orMissing(summary.lowest)}`);
  push(`Highest Recorded Pressure: ${orMissing(summary.highest)}`);
  push(`Total Test Duration: ${orMissing(summary.totalDuration)}`);
  push(`Pressure Chart Attached / Reference: ${orMissing(summary.chartAttached)}`);
  push(`Bleed-Off Volume: ${orMissing(summary.bleedOffVol)}`);
  push(`Leaks Observed (user-entered): ${yn(summary.leaks)}`);
  push(`Retest Required (user-entered): ${yn(summary.retestRequired)}`);
  push(`Components Retested: ${polishedOrMissing(summary.componentsRetested)}`);
  push(`Final User-Entered Test Status: ${orMissing(summary.finalStatus)}`);
  push(`Remarks: ${polishedOrMissing(summary.remarks)}`);

  // 14. Observations / Exceptions / Follow-Up
  sec(lines, "Observations / Exceptions / Follow-Up");
  push(`Observations: ${polishedOrMissing(obs.observations)}`);
  push(`Exceptions: ${polishedOrMissing(obs.exceptions)}`);
  push(`Leaks Noted: ${polishedOrMissing(obs.leaksNoted)}`);
  push(`Equipment Issues: ${polishedOrMissing(obs.equipmentIssues)}`);
  push(`Retests Required: ${polishedOrMissing(obs.retestsRequired)}`);
  push(`Repairs Completed: ${polishedOrMissing(obs.repairsCompleted)}`);
  push(`Repairs Pending: ${polishedOrMissing(obs.repairsPending)}`);
  push(`Follow-Up Required: ${polishedOrMissing(obs.followUpRequired)}`);
  push(`Personnel Notified: ${polishedOrMissing(obs.personnelNotified)}`);
  push(`Rough Notes: ${polishedOrMissing(obs.roughNotes)}`);
  push("");
  push("Observations are recorded as entered. This tool does not assign blame, does not invent root cause, and uses 'under review' wording when the user indicates the cause is unknown.");

  // 15. Witness / Review / Approval Placeholders
  sec(lines, "Witness / Review Placeholders");
  push(`Subsea Engineer: ${orMissing(witness.subseaEng)}`);
  push(`BOP Technician: ${orMissing(witness.bopTech)}`);
  push(`Toolpusher: ${orMissing(witness.toolpusher)}`);
  push(`OIM / Rig Manager: ${orMissing(witness.oim)}`);
  push(`Company Representative: ${orMissing(witness.companyRep)}`);
  push(`Client Representative: ${orMissing(witness.clientRep)}`);
  push(`Third-Party Witness: ${orMissing(witness.thirdParty)}`);
  push(`Date / Time Reviewed: ${orMissing(witness.reviewedAt)}`);
  push(`Comments: ${polishedOrMissing(witness.comments)}`);
  push("");
  push("Witness / review entries are placeholders only. Signatures and approval are recorded outside this tool. Approval is not implied unless the user records reviewed / approved status.");

  // Additional rough notes
  if (notes.roughNotes) {
    sec(lines, "Additional Rough Notes");
    push(polishedOrMissing(notes.roughNotes));
  }

  // Special warning
  sec(lines, "Subsea BOP Test Report Special Warning");
  push("Draft Subsea BOP Test Report support only. This tool records user-entered test information and does not certify BOP condition, component integrity, control-system reliability, well safety, regulatory compliance, or operational acceptance. All subsea BOP test results must be verified, interpreted, witnessed, and approved by competent personnel under approved procedures, manufacturer requirements, client requirements, and applicable regulations.");

  return lines.join("\n");
}

export const subseaBopChecklist = (s) => {
  const out = [];
  const h = s.header || {}, stack = s.stack || {}, scope = s.scope || {}, summary = s.summary || {}, witness = s.witness || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.procedureRef) out.push("Test procedure reference missing");
  if (!stack.stackId) out.push("Stack ID missing");
  if (!scope.startTime) out.push("Test start time missing");
  if (!scope.endTime) out.push("Test end time missing");
  if (!witness.subseaEng && !witness.companyRep && !witness.clientRep && !witness.toolpusher) out.push("Witness / review fields missing");
  const components = Array.isArray(s.components) ? s.components : [];
  if (!components.length) out.push("Component status fields missing — add at least one component row");
  if (!summary.chartAttached) out.push("Pressure chart reference missing");
  if (!summary.finalStatus) out.push("Final user-entered status missing");
  return out;
};

export const SUBSEA_BOP_QUICK_PHRASES = [
  "Test performed as per approved procedure.",
  "Recorded status only.",
  "Pressure held as recorded.",
  "Pressure drop observed as recorded.",
  "Leak observed as recorded.",
  "No leak observed as recorded.",
  "Retest required as recorded.",
  "Retest completed as recorded.",
  "Results pending supervisor review.",
  "Subsea engineer notified.",
  "Company representative witnessed.",
  "Chart reference entered.",
  "Follow-up required.",
  "Repairs pending.",
  "No interpretation entered.",
];

export const SUBSEA_BOP_COMPONENT_TYPES = [
  "Annular preventer",
  "Upper pipe ram",
  "Middle pipe ram",
  "Lower pipe ram",
  "Blind shear ram",
  "Casing shear ram",
  "Test ram",
  "Variable bore ram",
  "Choke valve",
  "Kill valve",
  "HCR valve",
  "Wellhead connector",
  "LMRP connector",
  "Other",
];

export const SUBSEA_BOP_TEST_TYPES = [
  "Full subsea BOP pressure test",
  "Function test",
  "Component test",
  "Post-maintenance test",
  "Regulatory test",
  "Client-required test",
  "Other",
];
